import {BehaviorSubject, Observable} from "rxjs";
import { Injectable } from '@angular/core';
import { Point } from "@angular/cdk/drag-drop";
import {GreenImageData, SprinklerProfile, Entity, Rectangle, IgpPlayground, SaveSprinklerModel, SaveSprinklerProfile, Color} from "./custom-types";
import { ImageDataService } from "./image-data-service";
import {TimerService} from "../services/timer.service";
import { NgxUiLoaderService } from "ngx-ui-loader";

@Injectable({
    providedIn: 'root'
})

export class SprinklerBookService {

    canvasContainer: HTMLElement;
    sprinklerProfiles: SprinklerProfile[] = [];
    depths: number[];
    copyrightYear: string = "";
    widthRatio: number = 1;
    heightRatio: number = 1;
    resizeWidth: number = 825;
    resizeHeight: number = 600;
    isShrinked: boolean = false;
    private _progress: BehaviorSubject<number> = new BehaviorSubject(0);
    public progressUpdates: Observable<number> = this._progress.asObservable()

    private set progress(value:number) {
        this._progress.next(value);
    }

    private get progress() {
        return this._progress.value;
    }

    constructor(
        private imageDataService: ImageDataService
    ) {

    }

    reset() {
        this.sprinklerProfiles = [];
        this.depths = [];
        this.copyrightYear = "";
        this.widthRatio = 1;
        this.heightRatio = 1;
        this.resizeWidth = 825;
        this.resizeHeight = 600;
        this.isShrinked = false;
        this.progress = 0
    }

    async sprinklerBook(canvasContainer:HTMLElement, sprinklerFiles: File[], depthFile: File, copyrightYear: string) : Promise<SprinklerProfile[]> {
        this.progress = 0
        this.canvasContainer = canvasContainer
        this.copyrightYear = copyrightYear
        console.time("LOAD-TIME")
        this.sprinklerProfiles = await this.loadFiles(sprinklerFiles, depthFile, 100)
        console.timeEnd("LOAD-TIME")
        this.progress = 100
        return this.sprinklerProfiles
    }

    async loadFiles(sprinklerFiles: File[], depthFile: File, progressCap: number = 100) {
        console.time("DEPTH-TIME")
        this.depths = await this.readDepthFile(depthFile)
        console.timeEnd("DEPTH-TIME")
        let sprinklerProfiles:SprinklerProfile[] = []
        sprinklerFiles = sprinklerFiles.filter(f => this.isValidSprinklerFile(f))
        console.time("SPRINKLER-PROFILES-TIME")
        const progressPerFile = (progressCap)/Math.max(sprinklerFiles.length, 1)

        for (let i=0; i< sprinklerFiles.length; i++) {
            let profile = await this.processSprinklerFile(sprinklerFiles[i], this.depths[i], progressPerFile, `${i}`)
            sprinklerProfiles.push(profile)
        }

        // sprinklerProfiles = await Promise.all(sprinklerFiles.map((f, i) => {
        //     return this.processSprinklerFile(sprinklerFiles[i], this.depths[i], progressPerFile, `${i}`)
        // }))
        this.progress = 100

        // for(let i=0; i<sprinklerFiles.length; i++) {
        //     console.log(`************--${i + 1} - START--************`)
        //     let sprinklerProfile = await this.processSprinklerFile(sprinklerFiles[i], this.depths[i], progressPerFile)
        //     sprinklerProfiles.push(sprinklerProfile)
        //     // this.progress += progressPerFile
        //     console.log(`************--${i + 1} - END--************`)
        // }
        console.timeEnd("SPRINKLER-PROFILES-TIME")
        return sprinklerProfiles
    }

    async processSprinklerFile(file:File, depth:number, progressPerFile:number, id:string) : Promise<SprinklerProfile> {
        this.progress += progressPerFile * 20/100
        let playground = this.newPlayground()  // temporary canvas to do calculations and create images
        console.time(`IMG${id}-`+"READ-SPRINKLER-TIME")
        // read ImageData from uploaded file
        let image:ImageData = await this.readSprinklerFile(file, playground)
        this.progress += progressPerFile * 10/100
        console.timeEnd(`IMG${id}-`+"READ-SPRINKLER-TIME")
        // console.time(`IMG${id}-`+"GREEN-IMAGE-TIME")
        // // copying ImageData to GreenImageData. top/bottom/left/right are empty
        // let data:GreenImageData = this.imageDataService.greenImage(image)
        // this.progress += progressPerFile * 10/100
        // console.timeEnd(`IMG${id}-`+"GREEN-IMAGE-TIME")
        // console.time(`IMG${id}-`+"BOUNDS-TIME")
        // // set b/t/l/r points for the GreenImageData
        // this.getBounds(data)
        // this.progress += progressPerFile * 20/100
        // console.timeEnd(`IMG${id}-`+"BOUNDS-TIME")
        console.time(`IMG${id}-`+"SINGLE-PROFILE-TIME")
        let sprinklerProfile: SprinklerProfile
        try {
            sprinklerProfile = await this.getSprinklerProfile(image, depth, this.copyrightYear, playground, id)
        } catch (e) {
            throw new Error(`${file.name}: ${e instanceof Error ? e.message : e}`)
        }
        sprinklerProfile.originalFileName = file.name.split('.').slice(0, -1).join('.')
        this.progress += progressPerFile * 70/100
        console.timeEnd(`IMG${id}-`+"SINGLE-PROFILE-TIME")
        return sprinklerProfile
    }

    async readDepthFile(depthFile:File) : Promise<any> {
        //to check for csv reader packages
        return new Promise((resolve, reject) => {
            let csvReader = new FileReader()
            csvReader.readAsText(depthFile)
            csvReader.onload = () => {
                let content = csvReader.result as string
                let csvRows = content?.split(/[\r\n]+/);
                let csvData = csvRows.map((row) => {
                    if(parseInt(row.split(',')[0])){
                        return parseInt(row.split(',')[0])
                    }else{
                        console.warn(`${row.split(',')[0]} is not a number`);
                        return 0;
                    }
                })

                console.log(csvData, '***csv-depth')
                resolve(csvData)
            }
        })
    }

    async readSprinklerFile(sprinklerFile: File, playground:IgpPlayground) {
        let imageData = await this.getImageData(sprinklerFile, playground)
        return imageData
    }

    newPlayground():IgpPlayground {
        return {
            canvas: document.createElement('canvas'),
            image: document.createElement("img")
        }
    }

    async getImageData(imgFile: File, playground:IgpPlayground) : Promise<ImageData> {
        return new Promise((resolve, reject) =>{
            let imgReader = new FileReader();
            imgReader.readAsDataURL(imgFile)
            imgReader.onload = () => {
                let img = playground.image
                let canvas = playground.canvas
                img.src = imgReader.result as string
                img.onload = () => {
                    let imgData: ImageData
                    canvas.width = img.width
                    canvas.height = img.height
                    let ctx = <CanvasRenderingContext2D>canvas.getContext('2d')
                    ctx.drawImage(img, 0, 0)
                    imgData = ctx.getImageData(0, 0, img.width, img.height)
                    resolve(imgData)
                }
            }
        })
    }

    isValidSprinklerFile(file: File): boolean {
        let fileName = file.name
        return (fileName.endsWith(".png")
          || fileName.endsWith(".jpeg")
          || fileName.endsWith(".jpg")
          || fileName.endsWith(".bmp")
        )
    }

    /**
     * Detect the contour line and calculate image edges(t/b/l/r)
     * @param {GreenImageData} data
     */
    getBounds(data: GreenImageData) : GreenImageData {
        let e: Entity = {} as Entity
        // start from center of the image and move downward in a straight line, looking for dark pixel
        let x = Math.round(data.width/2)
        for(let y = Math.round(data.height/2); y < data.height; y++) {
            let c = this.imageDataService.getColor(data, x, y)
            if(c.A != 0 && c.R < 100) {
                let point: Point = { x, y }
                e = this.getNewEntity(point, data, true)
                break;
            }
        }
        data.bottom = e.bottom
        data.top = e.top
        data.right = e.right
        data.left = e.left

        return data
    }

    getNewEntity(point: Point, image: GreenImageData, entire: boolean) : Entity {
        let entity: Entity = {
            initialPoint: point,
            entityArray: this.getEntityArray(image),
            count: 0,
            width: 0,
            height: 0,
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        }
        if(!entire) {
            entity = this.getContour(entity, image)
        }
        else {
            entity.entityArray = this.getEntireContour(entity.entityArray, image, point)
        }
        entity = this.getDimensions(entity)

        return entity
    }

    getNewEntityUsingContour(point: Point, image: GreenImageData, contour: Entity, entity:Entity, ts:TimerService) : Entity {
        // ts.start('new-entity-array')
        // ts.end('new-entity-array')
        ts.start('contour-with-contour')
        entity.initialPoint = point
        entity.entityArray = this.getEntireContourUsingContour(entity.entityArray, image, point, contour)
        ts.end('contour-with-contour')

        ts.start('entity-get-dimensions')
        entity = this.getDimensions(entity)
        ts.end('entity-get-dimensions')

        return entity
    }

    getEntityArray(data: GreenImageData) : Array<Uint8Array> {
        // return Array.from(
        //   {length: data.width},
        //   () => (new Array(data.height).fill(false))
        // )
        return Array.from(
          {length: data.width},
          () => (Uint8Array.from({length: data.height}))
        )
    }

    getContour(entity: Entity, image: GreenImageData) : Entity {
        let entityArray = entity.entityArray
        let done = false
        let operationPerformed = false
        let steppedBack = false
        let prevX = entity.initialPoint.x
        let prevY = entity.initialPoint.y
        let currX = entity.initialPoint.x
        let currY = entity.initialPoint.y
        let igX = -1
        let igY = -1

        entityArray[currX][currY] = 1

        while(!done) {
            if(!steppedBack) {
                if(currX + 1 < image.width) {
                    if(!operationPerformed && !entityArray[currX + 1][currY]) {
                        if(this.isTransparentPixel(currX + 1, currY, image)) {
                            entityArray[currX + 1][currY] = 1
                            prevX = currX
                            prevY = currY
                            currX++
                            operationPerformed = true
                        }
                    }
                }
                if(currY - 1 > 0) {
                    if(!operationPerformed && !entityArray[currX][currY - 1]) {
                        if(this.isTransparentPixel(currX, currY - 1, image)) {
                            entityArray[currX][currY - 1] = 1
                            prevX = currX
                            prevY = currY
                            currY--
                            operationPerformed = true
                        }
                    }
                }
                if(currX - 1 > 0) {
                    if(!operationPerformed && !entityArray[currX - 1][currY]) {
                        if(this.isTransparentPixel(currX - 1, currY, image)) {
                            entityArray[currX - 1][currY] = 1
                            prevX = currX
                            prevY = currY
                            currX--
                            operationPerformed = true
                        }
                    }
                }
                if(currY + 1 < image.height) {
                    if(!operationPerformed && !entityArray[currX][currY + 1]) {
                        if(this.isTransparentPixel(currX, currY + 1, image)) {
                            entityArray[currX][currY + 1] = 1
                            prevX = currX
                            prevY = currY
                            currY++
                            operationPerformed = true
                        }
                    }
                }
                if(!operationPerformed) {
                    igX = currX
                    igY = currY
                    currX = prevX
                    currY = prevY
                    steppedBack = true
                }
                else if(!operationPerformed) {
                    done = true
                } //Having confusion with above lines, need to check
                else {
                    operationPerformed = false
                }
            }
            else {
                if(currX + 1 < image.width && !(igX == currX + 1 && igY == currY)) {
                    if(!operationPerformed && !entityArray[currX + 1][currY]) {
                        if(this.isTransparentPixel(currX + 1, currY, image)) {
                            entityArray[currX + 1][currY] = 1
                            prevX = currX
                            prevY = currY
                            currX++
                            operationPerformed = true
                        }
                    }
                }
                if(currY - 1 > 0 && !(igX == currX && igY == currY - 1)) {
                    if(!operationPerformed && !entityArray[currX][currY - 1]) {
                        if(this.isTransparentPixel(currX, currY - 1, image)) {
                            entityArray[currX][currY - 1] = 1
                            prevX = currX
                            prevY = currY
                            currY--
                            operationPerformed = true
                        }
                    }
                }
                if(currX - 1 > 0 && !(igX == currX - 1 && igY == currY)) {
                    if(!operationPerformed && !entityArray[currX - 1][currY]) {
                        if(this.isTransparentPixel(currX - 1, currY, image)) {
                            entityArray[currX - 1][currY] = 1
                            prevX = currX
                            prevY = currY
                            currX--
                            operationPerformed = true
                        }
                    }
                }
                if(currY + 1 < image.height && !(igX == currX && igY == currY + 1)) {
                    if(!operationPerformed && !entityArray[currX][currY + 1]) {
                        if(this.isTransparentPixel(currX, currY + 1, image)) {
                            entityArray[currX][currY + 1] = 1
                            prevX = currX
                            prevY = currY
                            currY++
                            operationPerformed = true
                        }
                    }
                }
                if(!operationPerformed) {
                    done = true
                }
                else {
                    operationPerformed = false
                    steppedBack = false
                }
            }
        }
        return entity
    }

    /**
     * Detects all connected black pixels
     * * set `true` on the pixels positions to all the black pixels connected to the given point
     * used for detecting contour line and sprinkler entities
     * @param {Array<Uint8Array>} entityArray - a 2d array with the original image dimensions all places filled with `false`
     * @param {GreenImageData} image
     * @param point - A start point on the entity (Ex: bottom middle point of contour)
     */
    getEntireContour(entityArray: Array<Uint8Array>, image: GreenImageData, point: Point) : Array<Uint8Array> {
        let pointList: Point[] = []
        pointList.push(point)

        while(pointList.length > 0) {
            for(let i = 0; i < pointList.length; i++) {
                let {x, y} = pointList[i]
                let c = this.imageDataService.getColor(image, x, y)

                if(c.A != 0 && (c.R < 30 && c.G < 30 && c.B < 30) && !entityArray[x][y]) {
                    entityArray[x][y] = 1
                    pointList.push({ x: x-1, y:y })
                    pointList.push({ x: x+1, y:y })
                    pointList.push({ x:x, y: y-1 })
                    pointList.push({ x:x, y: y+1 })
                }
                pointList.splice(i, 1)
                i--
            }
        }

        return entityArray
    }

    getEntireContourUsingContour(entityArray: Array<Uint8Array>, image: GreenImageData, point: Point, contour: Entity) : Array<Uint8Array> {
        let pointList: Point[] = []
        pointList.push(point)

        while(pointList.length > 0) {
            for(let i = 0; i < pointList.length; i++) {
                let {x, y} = pointList[i]
                let c = this.imageDataService.getColor(image, x, y)

                /*
                Changed below condition because in the image at one place the c.R value is 55.
                */
                // if((c.R < 30 && c.G < 30 && c.B < 30) && c.A != 0 && !entityArray[x][y] && !contour.entityArray[x][y]) {
                if(c.A != 0 && ((c.R + c.G + c.B) < 90) && !entityArray[x][y] && !contour.entityArray[x][y]) {
                    entityArray[x][y] = 1
                    pointList.push({ x: x-1, y:y })
                    pointList.push({ x: x+1, y:y })
                    pointList.push({ x:x, y: y-1 })
                    pointList.push({ x:x, y: y+1 })
                }
                pointList.splice(i, 1)
                i--
            }
        }

        return entityArray
    }

    /**
     * Find left/right/top/bottom and width/height of given Entity (contour line or sprinkler)
     * @param entity
     */
    getDimensions(entity: Entity) : Entity {
        let entityArray = entity.entityArray
        let arrWidth = entityArray.length
        let arrHeight = entityArray[0].length

        //Find Bottom
        let botY = 0
        let found = false
        for(let y = arrHeight - 1; y > 0; y--) {
            for(let x = 0; x < arrWidth; x++) {
                if(entityArray[x][y]) {
                    botY = y
                    found = true
                    break;
                }
            }
            if(found) {
                break;
            }
        }
        entity.bottom = botY

        //Find Top
        let topY = 0
        found = false
        for(let y = 0; y < arrHeight; y++) {
            for(let x = 0; x < arrWidth; x++) {
                if(entityArray[x][y]) {
                    topY = y
                    found = true
                    break;
                }
            }
            if(found) {
                break;
            }
        }
        entity.top = topY

        //Find Left
        let leftX = 0
        found = false
        for(let x = 0; x < arrWidth; x++) {
            for(let y = 0; y < arrHeight; y++) {
                if(entityArray[x][y]) {
                    leftX = x
                    found = true
                    break;
                }
            }
            if(found) {
                break;
            }
        }
        entity.left = leftX

        //Find Right
        let rightX = 0
        found = false
        for(let x = arrWidth - 1; x > 0; x--) {
            for(let y = 0; y < arrHeight; y++) {
                if(entityArray[x][y]) {
                    rightX = x
                    found = true
                    break;
                }
            }
            if(found) {
                break;
            }
        }
        entity.right = rightX
        entity.width = entity.right - entity.left
        entity.height = entity.bottom - entity.top

        return entity
    }

    isTransparentPixel(x: number, y: number, image: GreenImageData) {
        let i = (y * (image.width * 4) + x * 4) + 3
        return image.pixels.data[i] == 0
    }

    addPoint(points: Point[], x: number, y: number) : Point[] {
        let p: Point = {
            x: x,
            y: y
        }
        points.push(p)
        return points
    }

    newSprinklerProfile(playground: IgpPlayground) : SprinklerProfile {
        let sprinklerProfile: SprinklerProfile = {
            playground: playground,
            test: {} as ImageData,
            copyrightYear: '',
            contour: {
                initialPoint: {} as Point,
                entityArray: [],
                count: 0,
                width: 0,
                height: 0,
                top: 0,
                bottom: 0,
                left: 0,
                right: 0
            },
            image: {
                pixels: {} as ImageData,
                width: 0,
                height: 0,
                top: 0,
                bottom: 0,
                right: 0,
                left: 0
            },
            bottomNormal: {} as Point,
            topNormal: {} as Point,
            sprinks: [],
            others: [],
            deadZones: [],
            topSprinklerLoc: {} as Point,
            botSprinklerLoc: {} as Point,
            topSprinkler: 0,
            botSprinkler: 0,
            yardLength: 0,
            greenDepth: 0,
            topCurvePoints: [],
            botCurvePoints: [],
            topCurveLength: 0,
            botCurveLength: 0,
            originalFileName: ""
        }
        return sprinklerProfile
    }

    async getSprinklerProfile(imageData: ImageData, greenDepth: number, copyrightYear: string, playground:IgpPlayground, id:string) : Promise<SprinklerProfile> {
        console.log(`IMG${id}-`+'generated-sprinkler-profile | START')

        let sprinklerProfile: SprinklerProfile = this.newSprinklerProfile(playground)
        sprinklerProfile.greenDepth = greenDepth
        sprinklerProfile.copyrightYear = copyrightYear
        sprinklerProfile.image = this.imageDataService.greenImage(imageData)
        sprinklerProfile.test = imageData
        console.time(`IMG${id}-`+"CONTOUR-TIME")
        // detect contour line
        const contourData = this.findContour(sprinklerProfile, imageData)
        sprinklerProfile = contourData.sprinklerProfile
        const startPoint = contourData.startPoint
        console.timeEnd(`IMG${id}-`+"CONTOUR-TIME")
        console.time(`IMG${id}-`+"NORMAL-TIME")
        // detect normal points
        sprinklerProfile = this.findNormal(sprinklerProfile)
        // @TODO - Canvas Drawing
        this.drawNormals(sprinklerProfile, playground)
        console.timeEnd(`IMG${id}-`+"NORMAL-TIME")
        console.time(`IMG${id}-`+"SPRINKLER-TIME")
        // detect sprinklers (profile.sprinks, profile.others(text))
        sprinklerProfile = this.findSprinklers(sprinklerProfile)
        console.timeEnd(`IMG${id}-`+"SPRINKLER-TIME")
        console.time(`IMG${id}-`+"CLOSEST-SPRINKLER-TIME")
        // detect sprinklers nearest to top and bottom normal points
        // topSprinkler/botSprinkler/topSprinklerLoc/botSprinklerLoc
        sprinklerProfile = this.findClosestSprinklers(sprinklerProfile)
        console.timeEnd(`IMG${id}-`+"CLOSEST-SPRINKLER-TIME")
        console.time(`IMG${id}-`+"WALK-1-TIME")
        this.isShrinked = false

        let polygonPoints: Point[] = [];
        polygonPoints = this.findSinglePixelContour(imageData, startPoint)
        sprinklerProfile = this.createWalkNew(sprinklerProfile, polygonPoints, sprinklerProfile.topNormal, sprinklerProfile.topSprinklerLoc, true)
        sprinklerProfile = this.createWalkNew(sprinklerProfile, polygonPoints, sprinklerProfile.bottomNormal, sprinklerProfile.botSprinklerLoc, false)
        return sprinklerProfile

        // create walk curve to top normal
        sprinklerProfile = await this.createWalk(playground, sprinklerProfile, sprinklerProfile.topNormal, sprinklerProfile.topSprinklerLoc, true)
        console.timeEnd(`IMG${id}-`+"WALK-1-TIME")
        console.time(`IMG${id}-`+"WALK-2-TIME")
        // create walk curve to bottom normal
        sprinklerProfile = await this.createWalk(playground, sprinklerProfile, sprinklerProfile.bottomNormal, sprinklerProfile.botSprinklerLoc, false)
        console.timeEnd(`IMG${id}-`+"WALK-2-TIME")

        console.log(`IMG${id}-`+'generated-sprinkler-profile | END | ', sprinklerProfile)
        return sprinklerProfile
    }

    findContour(sprinklerProfile: SprinklerProfile, imageData: ImageData) : {sprinklerProfile: SprinklerProfile, startPoint: Point} {
    let image = sprinklerProfile.image
    let yPos = 0
    let onBlack = false
    let centerX = Math.round(image.width / 2)
    let xOffsets = [0, 10] 
    let found = false
    let x = centerX
    for (let dx of xOffsets) {
        x = centerX + dx
        for (let y = Math.round(image.height / 2); y < image.height; y++) {
            let c = this.imageDataService.getColor(image, x, y)
            if ((c.R + c.G + c.B < 90) && c.A != 0 && !onBlack) {
                yPos = y
                onBlack = true
            }
            if ((!(c.R + c.G + c.B < 90) || c.A == 0) && onBlack) {
                yPos = y - 1
                found = true
                break
            }
        }
        if (found) break
        onBlack = false
    }
    if (!found) {
        console.warn("Contour start not found, using fallback")
        x = centerX
        yPos = Math.round(image.height / 2)
    }
    let point: Point = { x, y: yPos }
    let contour = this.getNewEntity(point, image, true)

    // non-convex greens (e.g. horseshoe) can have an empty center, so the downward scan misses the outline
    if (!this.isGreenOutline(contour, image)) {
        const fallback = this.findOutlineFromBottom(image)
        if (!fallback) {
            throw new Error('Could not detect the green outline in the sprinkler image')
        }
        point = fallback.point
        contour = fallback.contour
    }

    sprinklerProfile.contour = contour
    sprinklerProfile.yardLength = sprinklerProfile.contour.height / sprinklerProfile.greenDepth
    return { sprinklerProfile, startPoint: point }
}



    isOutlinePixel(image: GreenImageData, x: number, y: number) : boolean {
        let c = this.imageDataService.getColor(image, x, y)
        return c.A != 0 && c.R < 30 && c.G < 30 && c.B < 30
    }

    // sprinkler crosses, text and stray lines are much smaller than the green outline
    isGreenOutline(entity: Entity, image: GreenImageData) : boolean {
        return entity.width >= image.width * 0.25 && entity.height >= image.height * 0.25
    }

    findOutlineFromBottom(image: GreenImageData) : {point: Point, contour: Entity} | null {
        const columns = [0.5, 0.25, 0.75, 0.375, 0.625].map(r => Math.round(image.width * r))

        for(const x of columns) {
            for(let y = image.height - 1; y >= 0; y--) {
                if(!this.isOutlinePixel(image, x, y)) {
                    continue
                }
                const point: Point = { x, y }
                const contour = this.getNewEntity(point, image, true)
                if(this.isGreenOutline(contour, image)) {
                    return { point, contour }
                }
                // skip past the rejected entity and keep scanning up the column
                y = Math.min(y, contour.top)
            }
        }
        return null
    }

    findNormal(sprinklerProfile: SprinklerProfile) : SprinklerProfile {
        let test = sprinklerProfile.test
        let contour = sprinklerProfile.contour
        let onContour = false
        let lineFound = false
        let length = 0

        // let p: Point = {
        //     x: 0,
        //     y: 0
        // }
        // sprinklerProfile.bottomNormal = p
        // sprinklerProfile.topNormal = p
        let bottomNormal = sprinklerProfile.bottomNormal
        let topNormal = sprinklerProfile.topNormal

        for(let y = test.height - 1; y >= 0; y--) {
            for(let x = 0; x < test.width; x++) {
                if(contour.entityArray[x][y] && !onContour) {
                    length++
                    onContour = true
                    lineFound = true

                    bottomNormal.x = Math.round(x)
                    bottomNormal.y = Math.round(y)
                }
                else if(contour.entityArray[x][y] && onContour) {
                    length++
                }
                else if(!contour.entityArray[x][y] && onContour) {
                    break;
                }
            }
            if(lineFound)
                break;
        }

        if(!lineFound) {
            throw new Error('Could not detect the green outline in the sprinkler image')
        }

        bottomNormal.x += Math.round(length/2)
        // bottomNormal.y = bottomNormal.y

        // find the inner edge of the contour directly above the bottom normal point
        onContour = true
        for(let y = bottomNormal.y; y >= 0; y--) {
            if(!contour.entityArray[Math.round(bottomNormal.x)][y] && onContour) {
                onContour = false
            }
            else if(contour.entityArray[Math.round(bottomNormal.x)][y] && !onContour) {
                topNormal.x = Math.round(bottomNormal.x)
                topNormal.y = Math.round(y)
            }
        }

        if(topNormal.x === undefined) {
            throw new Error('Could not detect the top edge of the green above the bottom normal point')
        }

        // adjust topNormal to the outer edge of the contour
        onContour = true
        for(let y = topNormal.y; y >= 0; y--) {
            if(!contour.entityArray[topNormal.x][y] && onContour) {
                onContour = false
                topNormal.y = Math.round(y - 1)
            }
        }

        return sprinklerProfile
    }

    drawNormals(sprinklerProfile: SprinklerProfile, playground:IgpPlayground) {
        let test = sprinklerProfile.test
        let canvas = playground.canvas
        canvas.width = test.width
        canvas.height = test.height
        let ctx = <CanvasRenderingContext2D>canvas.getContext("2d")
        ctx.putImageData(test, 0, 0)

        this.drawNormal(ctx, sprinklerProfile.topNormal, sprinklerProfile.bottomNormal)
        sprinklerProfile.test = ctx.getImageData(0, 0, canvas.width, canvas.height)
    }

    drawNormal(ctx: CanvasRenderingContext2D, topNormal: Point, bottomNormal: Point, widthRatio: number = 1, heightRatio: number = 1, isShrinked: boolean = false) {
        ctx.lineWidth = 3
        if(isShrinked)
            ctx.lineWidth = 1
        ctx.moveTo(bottomNormal.x * widthRatio, bottomNormal.y * heightRatio)
        ctx.lineTo(topNormal.x * widthRatio, topNormal.y * heightRatio)
        ctx.stroke()
    }

    findSprinklers(sprinklerProfile: SprinklerProfile) : SprinklerProfile {
        let ts:TimerService = new TimerService()
        let sprinks: Rectangle[] = []  // sprinkler cross bounds
        let others: Rectangle[] = []  // other things like text next to sprinkler cross
        let deadZones: Array<Point[]> = []
        
        let image = sprinklerProfile.image
        let contour = sprinklerProfile.contour

        console.log(`findSprinklers: size: ${image.width} x ${image.height}`)
        let entity: Entity = {
            initialPoint: {x:0, y:0},
            entityArray: this.getEntityArray(image),
            count: 0,
            width: 0,
            height: 0,
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        }

        for(let y = 0; y < image.height; y++) {
            for(let x = 0; x < image.width; x++) {
                let contains = false
                let newRight = 0

                for(let i = 0; i < sprinks.length; i++) {
                    let isInside = this.isPointInRect(sprinks[i], x, y);
                    if(isInside) {
                        contains = true
                        newRight = sprinks[i].x + sprinks[i].width
                        break;
                    }
                }
                for(let i = 0; i < others.length; i++) {
                    let isInside = this.isPointInRect(others[i], x, y)
                    if(isInside) {
                        contains = true
                        newRight = others[i].x + others[i].width
                        break;
                    }
                }

                if(contour.entityArray[x][y]) {
                    contains = true
                    newRight = x
                }

                let c = this.imageDataService.getColor(image, x, y)

                // black pixel and not in contour line => could be sprinkler or text
                if(!contains && (c.R + c.G + c.B < 90) && c.A != 0 && !contour.entityArray[x][y]) {
                    let point: Point = {
                        x: Math.round(x),
                        y: Math.round(y)
                    }

                    ts.start('newContour');
                    ts.start('reset-entity-array')
                    entity.entityArray.map(a => a.fill(0))
                    ts.end('reset-entity-array')
                    this.getNewEntityUsingContour(point, image, contour,entity, ts)
                    ts.end('newContour');

                    let ratio = entity.width/entity.height

                    let rect: Rectangle = {
                        x: Math.round(entity.left - 1),
                        y: Math.round(entity.top - 1),
                        width: Math.round(entity.width + 2),
                        height: Math.round(entity.height + 2),
                        x2: Math.round(entity.left + entity.width + 1),
                        y2: Math.round(entity.top + entity.height + 1)
                    }
                    if(ratio > .90 && ratio < 1.1 && entity.width < 300 && entity.height < 300) {
                        sprinks.push(rect)
                    }
                    else {
                        others.push(rect)
                    }

                    x = entity.right
                }

                if(contains) {
                    x = newRight
                }
            }
        }

        console.log(`findSprinklers: ${ts}`)

        sprinklerProfile.sprinks = sprinks
        sprinklerProfile.others = others
        sprinklerProfile.deadZones = deadZones

        console.log('Sprinklers', sprinks)

        return sprinklerProfile
    }

    isPointInRect(rectangle: Rectangle, x: number, y: number) : boolean {
        return !(x < rectangle.x || x > rectangle.x2 ||
          y < rectangle.y || y > rectangle.y2);

        // ctx.beginPath()
        // ctx.rect(rectangle.x, rectangle.y, rectangle.width, rectangle.height)
        // return ctx.isPointInPath(x, y)
    }

    /**
     * Detect sprinklers closest to the normal points
     * @param sprinklerProfile
     */
    findClosestSprinklers(sprinklerProfile: SprinklerProfile) : SprinklerProfile {
        let closestToTopSprinkler: number = 0
        let secondClosestToTopSprinkler: number = 0
        let closestToBotSprinkler: number = 0
        let secondClosestToBotSprinkler: number = 0

        let topSprinklerLoc = sprinklerProfile.topSprinklerLoc
        let botSprinklerLoc = sprinklerProfile.botSprinklerLoc
        let topSprinkler = sprinklerProfile.topSprinkler
        let botSprinkler = sprinklerProfile.botSprinkler

        let closestTop = { index: -1, distance: Infinity };
        let secondTop = { index: -1, distance: Infinity };
        let closestBottom = { index: -1, distance: Infinity };
        let secondBottom = { index: -1, distance: Infinity };

        let sprinks = sprinklerProfile.sprinks
        let topNormal = sprinklerProfile.topNormal
        let bottomNormal = sprinklerProfile.bottomNormal
        for(let i = 0; i < sprinks.length; i++) {
            const centerX = sprinks[i].x + sprinks[i].width/2;
            const centerY = sprinks[i].y + sprinks[i].height/2;

            const topDist = Math.hypot(centerX - topNormal.x, centerY - topNormal.y);
            const botDist = Math.hypot(centerX - bottomNormal.x, centerY - bottomNormal.y);

            // Update top sprinklers
            if (topDist < closestTop.distance) {
                secondTop = { ...closestTop };
                closestTop = { index: i, distance: topDist };
            } else if (topDist < secondTop.distance) {
                secondTop = { index: i, distance: topDist };
            }

            // Update bottom sprinklers
            if (botDist < closestBottom.distance) {
                secondBottom = { ...closestBottom };
                closestBottom = { index: i, distance: botDist };
            } else if (botDist < secondBottom.distance) {
                secondBottom = { index: i, distance: botDist };
            }
        }

        if (closestTop.index === closestBottom.index) {
            if (closestTop.distance < closestBottom.distance) {
                closestBottom = secondBottom;
            } else {
                closestTop = secondTop;
            }
        }

        if (closestTop.index === -1 || closestBottom.index === -1) {
            throw new Error(`Expected at least 2 sprinklers in the sprinkler image, found ${sprinks.length}`)
        }

        topSprinkler = closestToTopSprinkler
        botSprinkler = closestToBotSprinkler

        topSprinklerLoc.x = Math.round(sprinks[closestTop.index].x) + Math.round(sprinks[closestTop.index].width/2)
        topSprinklerLoc.y = Math.round(sprinks[closestTop.index].y) + Math.round(sprinks[closestTop.index].height/2)
        botSprinklerLoc.x = Math.round(sprinks[closestBottom.index].x) + Math.round(sprinks[closestBottom.index].width/2)
        botSprinklerLoc.y = Math.round(sprinks[closestBottom.index].y) + Math.round(sprinks[closestBottom.index].height/2)

        sprinklerProfile.topSprinkler = topSprinkler
        sprinklerProfile.botSprinkler = botSprinkler
        sprinklerProfile.topSprinklerLoc = topSprinklerLoc
        sprinklerProfile.botSprinklerLoc = botSprinklerLoc

        return sprinklerProfile
    }

    async createWalk(playground:IgpPlayground, sprinklerProfile:SprinklerProfile, normal: Point, sprinkler: Point, isTop: boolean) : Promise<SprinklerProfile> {
        let yardLength = sprinklerProfile.yardLength

        if(isTop)
            sprinkler.y = sprinkler.y - 5
        else
            sprinkler.y = sprinkler.y + 5
            
        let curvePoints: Point[] = []
        curvePoints.push(sprinkler)

        let collisionNormal = {...normal}
        let curvePoint = {} as Point

        let currentIndex = 0
        let collisions = false
        let noError = true

        while(curvePoints[currentIndex] != normal && noError) {
            collisions = true
            collisionNormal = {...normal}

            if(isTop)
                collisionNormal.y = collisionNormal.y - 5
            else
                collisionNormal.y = collisionNormal.y + 5

            while(collisions) {
                let collisionResult = this.collision(curvePoints[currentIndex], collisionNormal, currentIndex==0, normal, sprinklerProfile)
                if(collisionResult == 1) {
                    // collision with contour. Move up or down
                    if(isTop) {
                        // move up
                        collisionNormal.y = collisionNormal.y - 1
                    } else {
                        // move down
                        collisionNormal.y = collisionNormal.y + 1
                    }
                }
                else if(collisionResult == 0) {
                    // no collision. draw the curve from sprinkler to the normal point
                    curvePoint = this.calculateCurvePoint(curvePoints[currentIndex], collisionNormal, currentIndex == 0, isTop, sprinklerProfile)
                    curvePoints.push(curvePoint)
                    currentIndex++
                    collisions = false
                }
                else if(collisionResult == -1) {
                    // error in collision detection
                    collisions = false
                    noError = false
                }
                else {
                    // could be collision with normal(2)
                    curvePoints.push(normal)
                    currentIndex++
                    collisions = false
                }
            }
        }
        if (noError) {
            isTop ? sprinklerProfile.topCurvePoints = curvePoints : sprinklerProfile.botCurvePoints = curvePoints

            let length = 0
            for(let i = 0; i < curvePoints.length - 1; i++) {
                length += Math.sqrt(Math.pow((curvePoints[i].y - curvePoints[i+1].y), 2) +
                  Math.pow((curvePoints[i].x - curvePoints[i + 1].x), 2))
            }

            length /= yardLength
            let intLength = Math.round(length)
            isTop ? sprinklerProfile.topCurveLength = intLength : sprinklerProfile.botCurveLength = intLength
        }

        return sprinklerProfile
    }

    collision(A: Point, B: Point, useDelay: boolean, normal: Point, sprinklerProfile: SprinklerProfile) : number {
        let image = sprinklerProfile.image
        let sprinks = sprinklerProfile.sprinks
        let others = sprinklerProfile.others

        if(B.y > image.height * 2 || B.y < (image.height * -2)) {
            console.error("Returning -1")
            return -1
        }

        let x0 = A.x
        let y0 = A.y

        let x1 = B.x
        let y1 = B.y

        let dx = Math.abs(x1 - x0)
        let dy = Math.abs(y1 - y0)

        let sx = 0
        let sy = 0

        if(x0 < x1)
            sx = 1
        else
            sx = -1

        // sx = (x0 < x1) ? 1 : -1
        
        if(y0 < y1)
            sy = 1
        else
            sy = -1

        // sy = (y0 < y1) ? 1 : -1

        let err = dx - dy

        let done = false

        let delay: number

        if(useDelay) {
            if(sprinks.length > 0)
                delay = sprinks[0].width/2 + 1
            else
                delay = 50
        }  
        else {
            delay = 0
        }

        // delay = useDelay ? (sprinks.length > 0 ? sprinks[0].width/2 + 1 : 50) : 0

        while(!done) {
            let e2 = 2 * err

            if(e2 > (dy * -1)) {
                err = err - dy
                x0 = x0 + sx
            }

            if(e2 < dx) {
                err = err + dx
                y0 = y0 + sy
            }

            if(x0 == normal.x && (y0 == normal.y || y0 == normal.y - 5 || y0 == normal.y + 5)) {
                return 2
            }

            if(x0 > 0 && x0 < image.width && y0 > 0 && y0 < image.height && !(x0 == x1 && y0 == y1)) {
                let c = this.imageDataService.getColor(image, Math.round(x0), Math.round(y0))

                if(delay < 1) {
                    // if((c.R + c.G + c.B < 90) && c.A != 0 && done != true) {
                    if((c.R + c.G + c.B < 90) && c.A != 0) {
                        let othersContains = false

                        for(let i = 0; i < others.length; i++) {
                            if(this.isPointInRect(others[i], x0, y0)) {
                                othersContains = true
                                break;
                            }
                        }

                        if(!othersContains) {
                            return 1
                        }
                    }
                }
                else {
                    delay--
                }
            }
            else {
                done = true
            }
        }
        
        return 0
    }

    calculateCurvePoint(start: Point, normalTarget: Point, useDelay: boolean, isTop: boolean, sprinklerProfile: SprinklerProfile) : Point {
        if(isTop) {
            normalTarget.y = normalTarget.y + 1
        }
        else {
            normalTarget.y = normalTarget.y - 1
        }

        let sprinks = sprinklerProfile.sprinks
        let others = sprinklerProfile.others
        let image = sprinklerProfile.image
        let yardLength = sprinklerProfile.yardLength

        let goingRight = start.x <= normalTarget.x
        let goingUp = start.y >= normalTarget.y

        let x0 = start.x
        let y0 = start.y

        let x1 = normalTarget.x
        let y1 = normalTarget.y

        let dx = Math.abs(x1 - x0)
        let dy = Math.abs(y1 - y0)

        let sx = 0
        let sy = 0

        if(x0 < x1)
            sx = 1
        else
            sx = -1

        // sx = (x0 < x1) ? 1 : -1
        
        if(y0 < y1)
            sy = 1
        else
            sy = -1

        // sy = (y0 < y1) ? 1 : -1

        let err = dx - dy

        let done = false

        let delay: number

        if(useDelay) {
            if(sprinks.length > 0)
                delay = sprinks[0].width/2 + 1
            else
                delay = 50
        }  
        else {
            delay = 0
        }

        // delay = useDelay ? (sprinks.length > 0 ? sprinks[0].width/2 + 1 : 50) : 0

        while(!done) {
            let e2 = 2 * err

            if(e2 > (dy * -1)) {
                err = err - dy
                x0 = x0 + sx
            }

            if(e2 < dx) {
                err = err + dx
                y0 = y0 + sy
            }

            if(x0 >= 0 && x0 < image.width && y0 >= 0 && y0 < image.height) {
                let c = this.imageDataService.getColor(image, Math.round(x0), Math.round(y0))

                if(delay < 1) {
                    // if((c.R + c.G + c.B < 90) && c.A != 0 && done != true) {
                    if((c.R + c.G + c.B < 90) && c.A != 0) {
                        let othersContains = false

                        for(let i = 0; i < others.length; i++) {
                            if(this.isPointInRect(others[i], x0, y0)) {
                                othersContains = true
                            }
                        }

                        if(!othersContains) {
                            done = true

                            let slope: number

                            slope = Math.atan((start.y - y0)/(x0 - start.x))
                            if(isTop) {
                                slope += Math.PI/2.0
                            }
                            else {
                                slope -= Math.PI/2.0
                            }

                            // slope = isTop ? slope += Math.PI/2.0 : slope -= Math.PI/2.0

                            let tempX = x0 + yardLength/2.0 * Math.cos(slope)
                            let tempY = y0 - yardLength/2.0 * Math.sin(slope)

                            if(Math.round(tempX) >= image.width) {
                                tempX = image.width - 1
                            }
                            if(Math.round(tempX) <= 1) {
                                tempX = 1
                            }

                            if(Math.round(tempY) >= image.height) {
                                tempY = image.height - 1
                            }
                            if(Math.round(tempY) <= 1) {
                                tempY = 1
                            }

                            //Adding Math.round to avoid float types
                            let curvePoint: Point = {
                                x: Math.round(tempX),
                                y: Math.round(tempY)
                            }
                            return curvePoint
                        }
                    }
                }
                else {
                    delay--
                }
            }
            else {
                let curvePoint: Point = {
                    x: -1,
                    y: -1
                }
                return curvePoint
            }
        }
        let curvePoint: Point = {
            x: -1,
            y: -1
        }
        return curvePoint
    }

    drawCurve(ctx: CanvasRenderingContext2D, curvePoints: Point[], isShrinked: boolean = false, widthRatio: number = 1, heightRatio: number = 1) {
        if (!curvePoints.length) {
            console.warn("Skipping drawCurve for empty curvePoints")
            return;
        }
        if(isShrinked) {
            ctx.lineWidth = 1
        }
        ctx.beginPath()
        ctx.moveTo(curvePoints[0].x * widthRatio, curvePoints[0].y * heightRatio);

        let i = 0
        if(curvePoints.length >= 3) {
            for (i = 1; i < curvePoints.length - 2; i ++)
            {
                var xc = ((curvePoints[i].x + curvePoints[i + 1].x) * widthRatio) / 2;
                var yc = ((curvePoints[i].y + curvePoints[i + 1].y) * heightRatio) / 2;
                ctx.quadraticCurveTo(curvePoints[i].x * widthRatio, curvePoints[i].y * heightRatio, xc, yc);
                ctx.stroke()
            }
        }
        // curve through the last two points
        ctx.quadraticCurveTo(curvePoints[i].x * widthRatio, curvePoints[i].y * heightRatio, curvePoints[i+1].x * widthRatio,curvePoints[i+1].y * heightRatio);

        ctx.stroke()
        ctx.closePath()
    }

    drawDistance(ctx: CanvasRenderingContext2D, rectangleTopLeft: Point, yardLength: number, image: GreenImageData, intLength: number, isShrinked: boolean = false,  widthRatio: number = 1, heightRatio: number = 1, increaseFont: boolean = false) {
        let boxTopLeft = {...rectangleTopLeft}
        if(rectangleTopLeft.x + 50 + yardLength < image.width) {
            if(rectangleTopLeft.y + 50 + yardLength < image.height) {
                boxTopLeft.x = Math.round(boxTopLeft.x + yardLength)
                boxTopLeft.y = boxTopLeft.y

                this.drawRectAndText(ctx, boxTopLeft.x, boxTopLeft.y, intLength, isShrinked, widthRatio, heightRatio, increaseFont)
            }
            else {
                boxTopLeft.x = Math.round(boxTopLeft.x + yardLength)
                boxTopLeft.y = boxTopLeft.y - 50

                this.drawRectAndText(ctx, boxTopLeft.x, boxTopLeft.y, intLength, isShrinked, widthRatio, heightRatio, increaseFont)
            }
        }
        else if(rectangleTopLeft.x - 50 - yardLength > 0) {
            if(rectangleTopLeft.y + 50 + yardLength < image.height) {
                boxTopLeft.x = Math.round(boxTopLeft.x - yardLength - 50)
                boxTopLeft.y = boxTopLeft.y

                this.drawRectAndText(ctx, boxTopLeft.x, boxTopLeft.y, intLength, isShrinked, widthRatio, heightRatio, increaseFont)
            }
            else {
                boxTopLeft.x = Math.round(boxTopLeft.x - yardLength - 50)
                boxTopLeft.y = boxTopLeft.y - 50

                this.drawRectAndText(ctx, boxTopLeft.x, boxTopLeft.y, intLength, isShrinked, widthRatio, heightRatio, increaseFont)
            }
        }
    }

    drawRectAndText(ctx: CanvasRenderingContext2D, x: number, y: number, length: number, isShrinked: boolean = false,  widthRatio: number = 1, heightRatio: number = 1, increaseFont: boolean = false) {
        console.log('Increase font:', increaseFont)
        ctx.font = `${increaseFont ? 45 : 39}px Arial`
        if(isShrinked) {
            ctx.lineWidth = 1
            ctx.font = `${(increaseFont ? 45 : 23)*(Math.max(heightRatio, widthRatio)*2)}px Arial`
        }

        ctx.beginPath()
        ctx.rect(x * widthRatio, y * heightRatio, (increaseFont ? (isShrinked ? 110 : 70) : 65) * widthRatio, (increaseFont ?  (isShrinked ? 110 : 70) : 65) * heightRatio)
        ctx.fillStyle = "white"
        ctx.fill()
        // ctx.lineWidth = 1
        ctx.strokeStyle = "black"
        ctx.stroke()

        ctx.beginPath()
        ctx.fillStyle = "black"
        if(length < 10) {
            ctx.fillText(length.toString(), (x + 15) * widthRatio, (y + (increaseFont ? (isShrinked ? 85 : 55) : 45)) * heightRatio)
        }
        else {
            ctx.fillText(length.toString(), (x + 5) * widthRatio, (y + (increaseFont ? (isShrinked ? 85 : 55) : 45)) * heightRatio)
        }
    }

    setCurrentSprinklerToCanvas(sprinklerProfile: SprinklerProfile, canvasContainer: HTMLElement) {
        let canvas = <HTMLCanvasElement>canvasContainer.querySelector(".image-canvas")
        canvas.width = sprinklerProfile.playground.canvas.width
        canvas.height = sprinklerProfile.playground.canvas.height
        let ctx = <CanvasRenderingContext2D>canvas.getContext('2d')

        ctx.drawImage(sprinklerProfile.playground.canvas, 0, 0)

        let widthRatio = this.widthRatio;
        let heightRatio = this.heightRatio;
        let isShrunk = (widthRatio != 1 || heightRatio != 1)

        this.drawNormal(ctx, sprinklerProfile.topNormal, sprinklerProfile.bottomNormal, widthRatio, heightRatio, isShrunk)
        this.drawCurve(ctx, sprinklerProfile.topCurvePoints, isShrunk, widthRatio, heightRatio)
        this.drawCurve(ctx, sprinklerProfile.botCurvePoints, isShrunk, widthRatio, heightRatio)
        this.drawDistance(ctx, sprinklerProfile.topSprinklerLoc, sprinklerProfile.yardLength, sprinklerProfile.image, sprinklerProfile.topCurveLength, isShrunk, widthRatio, heightRatio, (sprinklerProfile.test.width > 3000  || sprinklerProfile.test.height > 3000))
        this.drawDistance(ctx, sprinklerProfile.botSprinklerLoc, sprinklerProfile.yardLength, sprinklerProfile.image, sprinklerProfile.botCurveLength, isShrunk, widthRatio, heightRatio, (sprinklerProfile.test.width > 3000  || sprinklerProfile.test.height > 3000))

        this.resizeCanvas(canvas, sprinklerProfile)
    }



    resizeCanvas(canvas:HTMLCanvasElement, sprinklerProfile:SprinklerProfile) {
        // canvas.width = sprinklerProfile.test.width
        // canvas.height = sprinklerProfile.test.height
        // ctx.clearRect(0, 0, canvas.width, canvas.height)
        // ctx.putImageData(sprinklerProfile.test, 0, 0)
        //
        // if(!this.isShrinked) {
        //     let [widthRatio, heightRatio] = await this.resizeSprinkler(canvas, playground.image)
        //     this.widthRatio = widthRatio
        //     this.heightRatio = heightRatio
        //     this.isShrinked = true
        // }

        canvas.style.maxWidth = `100%`
        canvas.style.maxHeight = `100%`
        return

        let imageWidth = sprinklerProfile.playground.image.width;
        let imageHeight = sprinklerProfile.playground.image.height;

        let widthRatio = imageWidth / this.resizeWidth;
        let heightRatio = imageHeight / this.resizeHeight;

        let newWidth = imageWidth;
        let newHeight = imageHeight;
        if(heightRatio > widthRatio) {
            // scale image to match the height, and let the width have the gaps
            newHeight = this.resizeHeight;
            newWidth = (imageWidth/imageHeight)*newHeight;
        } else {
            // scale image to match the width, and let the height have the gaps
            newWidth = this.resizeWidth;
            newHeight = (imageHeight/imageWidth)*newWidth;
        }

        canvas.style.width = `${newWidth}px`
        canvas.style.height = `${newHeight}px`
    }

    resizeSprinkler(canvas: HTMLCanvasElement, image: HTMLImageElement) : Promise<number[]> {
        return new Promise((resolve, reject) => {
            image.src = canvas.toDataURL();

            let imageWidth = canvas.width
            let imageHeight = canvas.height
    
            let finalWidth = imageWidth
            let finalHeight = imageHeight

            if(imageHeight > this.resizeHeight) {
                let ratio = this.resizeHeight/imageHeight
                let tempWidth = ratio * imageWidth
                let tempHeight = this.resizeHeight
                let ratio2 =1
                if(tempWidth > this.resizeWidth) {
                    ratio2 = this.resizeWidth/tempWidth
                    tempWidth = this.resizeWidth
                }
                finalHeight = ratio2 * tempHeight
                finalWidth = tempWidth
            } else if(imageWidth > this.resizeWidth) {
                let ratio = this.resizeWidth/imageWidth
                finalHeight = ratio * imageHeight
                finalWidth = this.resizeWidth
            }
            let ctx = <CanvasRenderingContext2D>canvas.getContext('2d')
    
            canvas.width = finalWidth
            canvas.height = finalHeight
    
            image.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height)
                ctx.drawImage(image, 0, 0, finalWidth, finalHeight)
    
                let widthRatio = finalWidth/imageWidth
                let heightRatio = finalHeight/imageHeight
    
                resolve([widthRatio, heightRatio])
            }
        })
    }

    getSprinklerImages(sprinklerProfiles: SprinklerProfile[], canvasContainer: HTMLElement) : any[] {
        let sprinklerImages: any[] = []
        let canvas = <HTMLCanvasElement>canvasContainer.querySelector(".hidden-canvas")
        let ctx = <CanvasRenderingContext2D>canvas.getContext("2d")
        for(let i = 0; i < sprinklerProfiles.length; i++) {
            let sprinklerProfile = sprinklerProfiles[i]
            canvas.width = sprinklerProfile.image.width
            canvas.height = sprinklerProfile.image.height
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.putImageData(sprinklerProfile.image.pixels, 0, 0)
            this.drawNormal(ctx, sprinklerProfile.topNormal, sprinklerProfile.bottomNormal)
            this.drawCurve(ctx, sprinklerProfile.topCurvePoints)
            this.drawCurve(ctx, sprinklerProfile.botCurvePoints)
            this.drawDistance(ctx, sprinklerProfile.topSprinklerLoc, sprinklerProfile.yardLength, sprinklerProfile.image, sprinklerProfile.topCurveLength, (sprinklerProfile.test.width > 3000  || sprinklerProfile.test.height > 3000))
            this.drawDistance(ctx, sprinklerProfile.botSprinklerLoc, sprinklerProfile.yardLength, sprinklerProfile.image, sprinklerProfile.botCurveLength, (sprinklerProfile.test.width > 3000  || sprinklerProfile.test.height > 3000))
            let sprinklerImage: any = {
                name: sprinklerProfile.originalFileName,
                image: canvas.toDataURL()
            }
            sprinklerImages.push(sprinklerImage)
        }
        return sprinklerImages
    }

    async loadSprinklerBook(jsonFile: File, canvasContainer: HTMLElement, ngxLoader: NgxUiLoaderService, data: any, loadFromFile: boolean = false) : Promise<any[]> {
        this.canvasContainer = canvasContainer
        let currentGreen = 0
        let savedData : SaveSprinklerModel = {
            sprinklerProfiles: [],
            sprinklerConfig: undefined,
            clubName: "",
            courseName: "",
            copyrightYear: "",
            type: "",
            versionNumber: "",
            source: "",
            lastModifiedDate: "",
            clientID: "",
            courseID: ""
        }
        let linkClientCourse = false
        if(loadFromFile) {
            [savedData, linkClientCourse] = await this.readJsonFile(jsonFile, savedData, ngxLoader)
        }
        else {
            savedData = this.readDataFromSprinklerData(savedData, data)
        }
        this.sprinklerProfiles = await this.getSprinklerProfilesFromSaveSprinklerProfiles(savedData.sprinklerProfiles, canvasContainer)
        this.setCurrentSprinklerToCanvas(this.sprinklerProfiles[currentGreen], canvasContainer)
        return [this.sprinklerProfiles, savedData, linkClientCourse]
    }

    readDataFromSprinklerData(savedData: SaveSprinklerModel, sprinklerData: any) {
        savedData.sprinklerProfiles = JSON.parse(sprinklerData.SprinklerProfiles)
        savedData.sprinklerConfig = JSON.parse(sprinklerData.Config)
        savedData.copyrightYear = sprinklerData.CopyrightYear
        savedData.clubName = sprinklerData.ClientName
        savedData.courseName = sprinklerData.CourseName
        savedData.clientID = sprinklerData.ClientID
        savedData.courseID = sprinklerData.CourseID

        return savedData
    }

    readJsonFile(jsonFile: File, savedData: SaveSprinklerModel, ngxLoader: NgxUiLoaderService): Promise<[SaveSprinklerModel, boolean]> {
        return new Promise((resolve, reject) => {
            let newKeys = ['clientID', 'courseID']
            let jsonReader = new FileReader()
            let linkClientCourse = false
            jsonReader.readAsText(jsonFile)
            jsonReader.onload = () => {
                let jsonData = JSON.parse(jsonReader.result as string)
                let jsonKeys = Object.keys(jsonData)
                let savedDataKeys = Object.keys(savedData)
                if(savedDataKeys.length != jsonKeys.length && !(jsonKeys.includes(newKeys[0]) && jsonKeys.includes(newKeys[1]))) {
                    linkClientCourse = true
                    savedDataKeys = savedDataKeys.filter((v: any) => {
                        if(!newKeys.includes(v)) {
                            return v
                        }
                    })
                }
                if(jsonKeys.includes('type') && jsonData.type == 'SprinklerBook') {
                    if(JSON.stringify(jsonKeys.sort()) != JSON.stringify(savedDataKeys.sort())) {
                        alert("Invalid json file selected.")
                        ngxLoader.stop()
                    }
                    else {
                        savedData = jsonData
                        console.log(savedData, '***jsoncontent')
                        resolve([savedData, linkClientCourse])
                    }
                }
                else {
                    alert("Invalid json file selected.")
                    ngxLoader.stop()
                }
            }
        })
    }

    async getSprinklerProfilesFromSaveSprinklerProfiles(savedSprinklerProfiles: SaveSprinklerProfile[], canvasContainer: HTMLElement) : Promise<SprinklerProfile[]> {
        let segmentProfiles: SprinklerProfile[] = []
        
        for(let i=0; i<savedSprinklerProfiles.length; i++) {
            let savedSprinklerProfile = savedSprinklerProfiles[i]
            let playground = this.newPlayground()
            let segmentProfile: SprinklerProfile = {
                originalFileName: savedSprinklerProfile.originalFileName,
                image: this.imageDataService.greenImage(await this.convertStringToImageData(savedSprinklerProfile.image, playground)),
                playground: playground,
                test: {} as ImageData,
                copyrightYear: '',
                contour: {
                    initialPoint: {} as Point,
                    entityArray: [],
                    count: 0,
                    width: 0,
                    height: 0,
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0
                },
                bottomNormal: savedSprinklerProfile.bottomNormal,
                topNormal: savedSprinklerProfile.topNormal,
                botCurvePoints: savedSprinklerProfile.botCurvePoints,
                topCurvePoints: savedSprinklerProfile.topCurvePoints,
                botCurveLength: savedSprinklerProfile.botCurveLength,
                topCurveLength: savedSprinklerProfile.topCurveLength,
                botSprinklerLoc: savedSprinklerProfile.botSprinklerLoc,
                topSprinklerLoc: savedSprinklerProfile.topSprinklerLoc,
                sprinks: [],
                others: [],
                deadZones: [],
                topSprinkler: 0,
                botSprinkler: 0,
                yardLength: savedSprinklerProfile.yardLength,
                greenDepth: savedSprinklerProfile.greenDepth
            }
            segmentProfiles.push(segmentProfile)
            // let segmentProfile : SegmentProfile = savedSprinklerProfiles[i]
        }

        return segmentProfiles
    }

    convertStringToImageData(imgString: String, playground: IgpPlayground) : Promise<ImageData> {
        return new Promise((resolve, reject) => {
            let canvas = playground.canvas
            let ctx = <CanvasRenderingContext2D>canvas.getContext("2d")
            let image = new Image()
            image.src = "data:image/png;base64," + imgString
            let greenImg = playground.image
            greenImg.src = image.src
            image.onload = () => {
                canvas.width = image.width
                canvas.height = image.height
                ctx = <CanvasRenderingContext2D>canvas.getContext("2d")
                ctx.drawImage(image, 0, 0)
                resolve(ctx.getImageData(0, 0, canvas.width, canvas.height))
            }
        })
    }

    createWalkNew(sprinklerProfile:SprinklerProfile, polygonPoints: Point[], normal: Point, sprinkler: Point, isTop: boolean){
        const width = sprinklerProfile.image.pixels.width;
        const height = sprinklerProfile.image.pixels.height;
        const channels = 4; // fixed for ImageData

        const imageShape: [number, number, number] = [height, width, channels];
        
        const pointOnBorderNearToSprinklerLoc = this.findNearestPoint(sprinkler, polygonPoints);
        const pointOnBorderNearToNormal = this.findNearestPoint(normal, polygonPoints);

        // 2. Get the shortest path on the contour between bottomNormal and the nearest point found above
        const curvePointsOnBorder = this.getShortestPathBetweenPoints(polygonPoints, pointOnBorderNearToNormal.nearestPoint, pointOnBorderNearToSprinklerLoc.nearestPoint);
        const gapBwCurvePoints = Math.round(sprinklerProfile.yardLength / 2)

        // 3. Compute outward normals for these curve points relative to the polygon border contour
        const outwardNormals = this.computeOptimizedNormalsForParallelCurve(curvePointsOnBorder, polygonPoints, gapBwCurvePoints);

        // 4. Generate a parallel curve to the curvePoints offset by the distance found earlier, constrained by image size
        sprinklerProfile = this.generateMinDistanceCurve(curvePointsOnBorder, sprinklerProfile, pointOnBorderNearToNormal.nearestPoint, sprinkler, outwardNormals, imageShape, isTop)

        return sprinklerProfile
    }

    findNearestPoint(targetPoint: Point, borderPoints: Point[]): { nearestPoint: Point; distance: number } {
        let minDistance = Infinity;
        let nearestPoint: Point = borderPoints[0];
      
        for (const point of borderPoints) {
          const dx = point.x - targetPoint.x;
          const dy = point.y - targetPoint.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
      
          if (distance < minDistance) {
            minDistance = distance;
            nearestPoint = point;
          }
        }
      
        return { nearestPoint: nearestPoint, distance: minDistance };
    }

    pointsEqual(p1: Point, p2: Point): boolean {
        return p1.x === p2.x && p1.y === p2.y;
    }

    indexOfPoint(points: Point[], target: Point): number {
        for (let i = 0; i < points.length; i++) {
          if (this.pointsEqual(points[i], target)) {
            return i;
          }
        }
        return -1;
    }

    getShortestPathBetweenPoints(
        contourPoints: Point[],
        pt1: Point,
        pt2: Point
      ): Point[] {
        const idx1 = this.indexOfPoint(contourPoints, pt1);
        const idx2 = this.indexOfPoint(contourPoints, pt2);
      
        if (idx1 === -1 || idx2 === -1) {
          throw new Error("pt1 or pt2 not found in contourPoints");
        }
      
        // Path 1: from idx1 to idx2 clockwise
        let path1: Point[];
        if (idx1 <= idx2) {
          path1 = contourPoints.slice(idx1, idx2 + 1);
        } else {
          path1 = contourPoints.slice(idx1).concat(contourPoints.slice(0, idx2 + 1));
        }
      
        // Path 2: from idx2 to idx1 (opposite direction), then reversed
        let path2: Point[];
        if (idx2 <= idx1) {
          path2 = contourPoints.slice(idx2, idx1 + 1);
        } else {
          path2 = contourPoints.slice(idx2).concat(contourPoints.slice(0, idx1 + 1));
        }
        path2.reverse();
      
        // Choose the shortest path that starts at pt1 and ends at pt2
        if (path1.length <= path2.length) {
          return path1;
        } else {
          return path2;
        }
      }

    computeOptimizedNormalsForParallelCurve(
        curvePoints: Point[], 
        polygon: Point[],
        gapBwPoints: number
    ): Point[] {
        if (curvePoints.length < 2) return [];
        
        const requiredIndices = new Set<number>();
        for (let i = 0; i < curvePoints.length; i++) {
            requiredIndices.add(i);
        }

        const normals: Point[] = new Array(curvePoints.length);
        const polygonBounds = this.calculatePolygonBounds(polygon);
        
        const allTangents = this.computeSmoothedTangents(curvePoints);
        if (!allTangents || allTangents.length !== curvePoints.length) {
            throw new Error("Tangent computation failed");
        }
        
        requiredIndices.forEach(i => {
            const tangent = allTangents[i];
            const pCurr = curvePoints[i];
            
            if (!tangent) {
                console.warn(`Missing tangent at index ${i}, using default`);
                normals[i] = { x: 0, y: 0 }; // Default fallback
                return;
            }

            if (typeof tangent.x === 'undefined' || typeof tangent.y === 'undefined') {
                console.warn(`Invalid tangent at index ${i}, using default`);
                normals[i] = { x: 0, y: 0 }; // Default fallback
                return;
            }

            const normal = { 
                x: -tangent.y || 0,
                y: tangent.x || 0   
            };

            if (!this.isDefinitelyOutsideFast(pCurr, normal, polygon, polygonBounds)) {
                normals[i] = { x: -normal.x, y: -normal.y };
            } else {
                normals[i] = normal;
            }
        });

        return normals;
    }

    calculatePolygonBounds(polygon: Point[]): {minX: number, maxX: number, minY: number, maxY: number} {
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        
        for (const p of polygon) {
            minX = Math.min(minX, p.x);
            maxX = Math.max(maxX, p.x);
            minY = Math.min(minY, p.y);
            maxY = Math.max(maxY, p.y);
        }
        
        return { minX, maxX, minY, maxY };
    }

    computeSmoothedTangents(points: Point[]): Point[] {
        if (!points || points.length < 2) return [];
        
        const tangents: Point[] = new Array(points.length);
        const windowSize = Math.max(2, Math.floor(points.length * 0.05));
        
        for (let i = 0; i < points.length; i++) {
            const prev = points[Math.max(0, i - windowSize)];
            const next = points[Math.min(points.length - 1, i + windowSize)];
            
            // Ensure points exist
            if (!prev || !next) {
                tangents[i] = { x: 0, y: 0 };
                continue;
            }

            const tangent = {
                x: next.x - prev.x,
                y: next.y - prev.y
            };
            
            this.normalizeVector(tangent);
            
            // Smooth with previous tangent if available
            if (i > 0 && tangents[i-1]) {
                tangent.x = 0.7 * tangent.x + 0.3 * tangents[i-1].x;
                tangent.y = 0.7 * tangent.y + 0.3 * tangents[i-1].y;
                this.normalizeVector(tangent);
            }
            
            tangents[i] = tangent;
        }
        
        return tangents;
    }

    normalizeVector(v: {x: number, y: number}): void {
        if (!v) return;
        
        const length = Math.sqrt(v.x * v.x + v.y * v.y);
        if (length > 0) {
            v.x /= length;
            v.y /= length;
        } else {
            v.x = 0;
            v.y = 0;
        }
    }

    isDefinitelyOutsideFast(
        point: Point,
        normal: Point,
        polygon: Point[],
        bounds: {minX: number, maxX: number, minY: number, maxY: number}
    ): boolean {
        const testX = point.x + normal.x * 5;
        const testY = point.y + normal.y * 5;
        
        if (testX < bounds.minX || testX > bounds.maxX || 
            testY < bounds.minY || testY > bounds.maxY) {
            return true;
        }
        return !this.optimizedPointInPolygon(testX, testY, polygon);
    }

    optimizedPointInPolygon(
        x: number,
        y: number,
        polygon: Point[]
    ): boolean {
        return this.pointInPolygon({x, y}, polygon);
    }

    pointInPolygon(point: Point, polygon: Point[]): boolean {
        let inside = false;
        const x = point.x, y = point.y;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i].x, yi = polygon[i].y;
          const xj = polygon[j].x, yj = polygon[j].y;
          
          const intersect = ((yi > y) !== (yj > y)) &&
                            (x < (xj - xi) * (y - yi) / (yj - yi + 0.0000001) + xi);
          if (intersect) inside = !inside;
        }
        return inside;
    }

    generateMinDistanceCurve(
        curvePointsOnBorder: Point[],
        sprinklerProfile: SprinklerProfile,
        normal: Point, 
        sprinkler: Point,
        directionNormals: Point[],
        imageShape: [number, number, number],
        isTop: boolean
    ): SprinklerProfile{
        // curvePointsOnBorder - always start from the normal point to 
        const [height, width] = imageShape;
        const sprinklerCurvePts: Point[] = [];

        let yardLength = sprinklerProfile.yardLength
        const halfYardLength = Math.round(yardLength / 2)

        if (!directionNormals.length) {
            throw new Error('Could not trace the green outline between the normal point and the sprinkler')
        }

        const outsideMovedNormal = this.getOffsetPoint(directionNormals[0], curvePointsOnBorder[0], height, width)
            
        let curvePoints: Point[] = []
        curvePoints.push(sprinkler)

        let collisionNormal = {...normal}
        let curvePoint = {} as Point

        let currentIndex = 0
        let collisions = false
        let noError = true

        let count = 0
        let maxLoopLimit = 50

        while(curvePoints[currentIndex] != normal && noError) {
            if (count > maxLoopLimit) {
                console.warn('Reached max limit loop')
                break
            }
            count++
            for(let i = 0; i < curvePointsOnBorder.length; i++) { 
                const pt = curvePointsOnBorder[i];
                const directionalNormal = directionNormals[i];

                collisionNormal = {...pt}

                const {x, y} = this.getOffsetPoint(directionalNormal, pt, height, width)
                collisionNormal.x = x
                collisionNormal.y = y

                let collisionResult = this.collision(curvePoints[currentIndex], collisionNormal, currentIndex==0, outsideMovedNormal, sprinklerProfile)
                if(collisionResult == 0){
                    curvePoint = this.getOffsetPoint(directionalNormal, pt, height, width, halfYardLength)
                    curvePoint.x = Math.max(1, Math.min(curvePoint.x, width - 1));
                    curvePoint.y = Math.max(1, Math.min(curvePoint.y, height - 1));
                    curvePoints.push(curvePoint)
                    currentIndex++
                    break
                }
                else if(collisionResult == 2){
                    curvePoints.push(normal)
                    currentIndex++
                    break
                }
                if (i == curvePointsOnBorder.length - 1){
                    noError = false
                    break
                }
            }
        }
        if (noError) {
            isTop ? sprinklerProfile.topCurvePoints = curvePoints : sprinklerProfile.botCurvePoints = curvePoints
            let length = 0
            for(let i = 0; i < curvePoints.length - 1; i++) {
                length += Math.sqrt(Math.pow((curvePoints[i].y - curvePoints[i+1].y), 2) +
                  Math.pow((curvePoints[i].x - curvePoints[i + 1].x), 2))
            }
            length /= yardLength
            let intLength = Math.round(length)
            isTop ? sprinklerProfile.topCurveLength = intLength : sprinklerProfile.botCurveLength = intLength
        }
        return sprinklerProfile

    }

    generateParallelCurve(
        curvePoints: Point[],
        normals: Point[],
        imageShape: [number, number, number],
        distance: number,
        gapBwPoints: number
    ): Point[] {
        const requiredIndices = new Set<number>();
        for (let i = 0; i < curvePoints.length; i += gapBwPoints) {
            requiredIndices.add(i);
        }
        requiredIndices.add(curvePoints.length - 1); 

        const [height, width] = imageShape;
        const parallelCurve: Point[] = [];
        
        // Calculate total curve length for better distance distribution
        let totalLength = 0;
        const segmentLengths: number[] = [];
        for (let i = 1; i < curvePoints.length; i++) {
            const dx = curvePoints[i].x - curvePoints[i-1].x;
            const dy = curvePoints[i].y - curvePoints[i-1].y;
            const length = Math.sqrt(dx*dx + dy*dy);
            segmentLengths.push(length);
            totalLength += length;
        }

        const distancePerSegment = distance / totalLength;
        let accumulatedLength = 0;

        requiredIndices.forEach(i => {
            const pt = curvePoints[i];
            const normal = normals[i];

            if (!pt || !normal || typeof normal.x !== "number" || typeof normal.y !== "number") {
                console.warn(`Skipping index ${i} due to missing or invalid normal`);
                return;
            }
            
            const currentOffset = accumulatedLength * distancePerSegment;
            
            let newX = Math.round(pt.x + normal.x * currentOffset);
            let newY = Math.round(pt.y + normal.y * currentOffset);

            newX = Math.max(0, Math.min(newX, width - 1));
            newY = Math.max(0, Math.min(newY, height - 1));
            parallelCurve.push({ x: newX, y: newY });

            if (i > 0 && i < segmentLengths.length) {
                accumulatedLength += segmentLengths[i-1] * gapBwPoints;
            }
        })
        return parallelCurve;
    }

    getOffsetPoint(normal: Point, point: Point, height: number, width: number, offset: number = 5): {x: number, y: number}{
        let newX = Math.round(point.x + normal.x * offset);
        let newY = Math.round(point.y + normal.y * offset);

        newX = Math.max(0, Math.min(newX, width - 1));
        newY = Math.max(0, Math.min(newY, height - 1));

        return { x: newX, y: newY };
    }

    findSinglePixelContour(
        imageData: ImageData, 
        startPoint: Point
    ): Point[] {
        const width = imageData.width;
        const height = imageData.height;
        const data = imageData.data;
        
        const isBlack = (x: number, y: number): boolean => {
            if (x < 0 || x >= width || y < 0 || y >= height) return false; // Consider out-of-bounds as white
            let idx = (y * width + x) * 4;
            let c:Color = {
                R: imageData.data[idx++],
                G: imageData.data[idx++],
                B: imageData.data[idx++],
                A: imageData.data[idx++]
            }
            return c.A != 0 && (c.R < 30 && c.G < 30 && c.B < 30)
        };

        const contour: Point[] = [];
        let { x, y } = startPoint;
        
        // 8-directional movement (Moore neighborhood)
        // Directions: (indexed 0-7)
        // 5  6  7
        // 4  •  0
        // 3  2  1
        const directions = [
            { dx: 1, dy: 0 },   // 0: right
            { dx: 1, dy: 1 },    // 1: down-right
            { dx: 0, dy: 1 },    // 2: down
            { dx: -1, dy: 1 },   // 3: down-left
            { dx: -1, dy: 0 },   // 4: left
            { dx: -1, dy: -1 },  // 5: up-left
            { dx: 0, dy: -1 },   // 6: up
            { dx: 1, dy: -1 }    // 7: up-right
        ];

        if (!isBlack(x, y)) {
            throw new Error("Starting point is not on a black pixel");
        }

        let outwardDirIndex = -1;
        for (let i = 0; i < directions.length; i++) {
            const { dx, dy } = directions[i];
            if (!isBlack(x + dx, y + dy)) {
                outwardDirIndex = i;
                break;
            }
        }

        if (outwardDirIndex === -1) {
            console.warn("No white pixel found around the starting pixel")
            return [startPoint];
        }

        const firstPoint: Point = { x, y };
        contour.push(firstPoint);
        let currentPoint = firstPoint;
        let dirIndex = (outwardDirIndex - 1) % 8; // Start with neighbor to left of first white
        if (dirIndex < 0) dirIndex += 8; 

        let count = 0;
        const maxIterations = width * height * 2; // Prevent infinite loops

        while(count++ < maxIterations) {
            // Try directions starting from left of current outward direction
            let foundNext = false;
            let nextPoint: Point = currentPoint;
            let newDirIndex = dirIndex;

            for (let i = 0; i < 8; i++) {
                const testDir = (dirIndex - i + 8) % 8; // Search counter-clockwise
                const { dx, dy } = directions[testDir];
                const nx = currentPoint.x + dx;
                const ny = currentPoint.y + dy;

                // Check if this is a border pixel (black with white neighbor)
                if (isBlack(nx, ny)) {
                    // Verify it has at least one white neighbor
                    let hasWhiteNeighbor = false;
                    for (const { dx: edx, dy: edy } of directions) {
                        if (!isBlack(nx + edx, ny + edy)) {
                            hasWhiteNeighbor = true;
                            break;
                        }
                    }

                    if (hasWhiteNeighbor) {
                        nextPoint = { x: nx, y: ny };
                        newDirIndex = (testDir + 2) % 8; // Look rightward next
                        foundNext = true;
                        break;
                    }
                }
            }

            if (!foundNext) break;
            
            if (contour.length > 3 && 
                nextPoint.x === firstPoint.x && 
                nextPoint.y === firstPoint.y) {
                contour.push(nextPoint);
                break;
            }

            contour.push(nextPoint);
            currentPoint = nextPoint;
            dirIndex = newDirIndex;
        }

        return contour;
    }

    findCurvePointsDistance(curvePoints: Point[], yardLength: number): number{
        let length = 0
        for(let i = 0; i < curvePoints.length - 1; i++) {
            length += Math.sqrt(Math.pow((curvePoints[i].y - curvePoints[i+1].y), 2) +
                Math.pow((curvePoints[i].x - curvePoints[i + 1].x), 2))
        }
        length /= yardLength
        let intLength = Math.round(length)
        return intLength
    }
}